import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, UserPlus, Trash2 } from "lucide-react";
import { API } from "../../context/AuthContext";

export default function CollabDialog({ caseId, caseName, onClose }) {
  const [collabs, setCollabs] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/cases/${caseId}/collaborators`).then((res) => setCollabs(res.data)).catch(() => {});
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/cases/${caseId}/collaborators`, { email, role });
      setEmail("");
      load();
      toast.success(`${email.trim()} can now ${role === "editor" ? "view and edit" : "view"} this timeline once they log in.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add collaborator.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (em) => {
    try {
      await axios.delete(`${API}/cases/${caseId}/collaborators/${encodeURIComponent(em)}`);
      setCollabs((prev) => prev.filter((c) => c.email !== em));
      toast.success("Collaborator removed.");
    } catch {
      toast.error("Couldn't remove collaborator.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="collab-dialog">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#131f3a] border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-4 shadow-2xl">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide font-display">Collaborators — {caseName}</h3>
          <button onClick={onClose} data-testid="collab-close-btn" className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-gray-400 leading-relaxed">
          Invite a lawyer, advocate, or family member by email. <span className="text-gray-300">Viewers</span> can see everything; <span className="text-gray-300">Editors</span> can also add and remove events and files.
        </p>
        <form onSubmit={add} className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="their@email.com"
            data-testid="collab-email-input"
            className="flex-1 bg-gray-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-gray-100 focus:border-[#f59e0b]/50 focus:outline-none"
          />
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="collab-role-select" className="bg-gray-950 border border-white/10 rounded-lg px-2 py-2 text-xs text-gray-300 focus:outline-none">
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
          </select>
          <button type="submit" disabled={busy} data-testid="collab-add-btn" className="flex items-center gap-1 bg-[#f59e0b] hover:bg-[#fbbf24] text-gray-950 font-bold text-[10px] font-mono px-3 py-2 rounded-lg transition-colors duration-200 disabled:opacity-50">
            <UserPlus className="w-3 h-3" /> ADD
          </button>
        </form>
        <div className="space-y-2">
          {collabs.length === 0 ? (
            <p className="text-[11px] text-gray-500 italic text-center py-3" data-testid="collab-empty">No collaborators yet — this timeline is yours alone.</p>
          ) : (
            collabs.map((c) => (
              <div key={c.email} className="flex items-center justify-between bg-gray-950 border border-white/5 rounded-xl px-3 py-2" data-testid={`collab-row-${c.email}`}>
                <div>
                  <div className="text-xs text-gray-200 font-mono">{c.email}</div>
                  <div className="text-[9px] text-gray-500 font-mono uppercase">{c.role}</div>
                </div>
                <button onClick={() => remove(c.email)} data-testid={`collab-remove-${c.email}`} className="text-gray-500 hover:text-rose-400 transition-colors duration-200">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
